/**
 * NotificationBell – pending consent requests (REQ-008) and in-app notices.
 *
 * The bell used to count consent requests only and navigate straight to them.
 * That left nowhere to say anything else - a weekly reminder that an emergency
 * record is unfinished has no push subscription to rely on and no page of its
 * own to live on. It opens a list now, and a consent request is one entry in it.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useConsentRequests } from "@/hooks/useConsentRequests";
import { useAppNotifications } from "@/hooks/useAppNotifications";

export function NotificationBell() {
  const { pendingCount } = useConsentRequests();
  const { notifications, unread, markRead } = useAppNotifications();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const total = pendingCount + unread;

  useEffect(() => {
    if (!open) return;
    const onClickAway = (event: MouseEvent) => {
      if (!panelRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickAway);
    return () => document.removeEventListener("mousedown", onClickAway);
  }, [open]);

  const openNotification = (id: string, actionUrl?: string) => {
    void markRead(id);
    setOpen(false);
    if (actionUrl) navigate(actionUrl);
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-full hover:bg-gray-100 transition-colors"
        data-testid="notification-bell"
        aria-label={`Notifications${total > 0 ? ` (${total} unread)` : ""}`}
        aria-expanded={open}
      >
        <svg
          className="w-6 h-6 text-gray-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
        {total > 0 && (
          <span
            data-testid="notification-count"
            className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center bg-red-500 text-white text-xs font-bold rounded-full animate-pulse"
          >
            {total > 9 ? "9+" : total}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-panel"
          className="absolute right-0 z-50 mt-2 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 bg-white shadow-xl"
        >
          <div className="border-b border-gray-100 px-4 py-3">
            <p className="text-sm font-semibold text-gray-900">Notifications</p>
          </div>

          <ul className="max-h-96 overflow-y-auto">
            {/* Consent requests first: someone is waiting on an answer. */}
            {pendingCount > 0 && (
              <li>
                <button
                  type="button"
                  data-testid="notification-consent"
                  onClick={() => {
                    setOpen(false);
                    navigate("/traveler/consent-requests");
                  }}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50"
                >
                  <p className="text-sm font-medium text-gray-900">
                    {pendingCount === 1
                      ? "1 access request is waiting"
                      : `${pendingCount} access requests are waiting`}
                  </p>
                  <p className="text-xs text-gray-600">
                    Someone has asked to see your health data.
                  </p>
                </button>
              </li>
            )}

            {notifications.map((notification) => (
              <li key={notification.id}>
                <button
                  type="button"
                  data-testid="notification-item"
                  onClick={() =>
                    openNotification(notification.id, notification.actionUrl)
                  }
                  className={`w-full px-4 py-3 text-left hover:bg-gray-50 ${
                    notification.readAt ? "opacity-60" : ""
                  }`}
                >
                  <p className="text-sm font-medium text-gray-900">
                    {notification.title}
                  </p>
                  <p className="text-xs text-gray-600">{notification.body}</p>
                </button>
              </li>
            ))}

            {pendingCount === 0 && notifications.length === 0 && (
              <li className="px-4 py-6 text-center text-sm text-gray-500">
                Nothing new
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
